<template>
    <div class="bg-white dark:bg-surface-card">
        <!-- Hero Section -->
        <section class="relative bg-white py-16 md:py-24 overflow-hidden dark:bg-surface-card">
            <!-- Decorative blur elements -->
            <div class="absolute inset-0 overflow-hidden pointer-events-none">
                <div class="absolute -top-20 -right-20 w-96 h-96 bg-[#047274]/15 rounded-full blur-3xl"></div>
                <div class="absolute -bottom-20 -left-20 w-96 h-96 bg-orange-500/15 rounded-full blur-3xl"></div>
                <div class="absolute top-1/3 right-1/4 w-64 h-64 bg-[#0a9396]/10 rounded-full blur-3xl"></div>
            </div>

            <div class="container relative z-10">
                <div class="max-w-6xl mx-auto">
                    <div class="text-center mb-12">
                        <h1 class="text-4xl md:text-5xl font-bold text-gray-900 mb-6 dark:text-txt-headings">GROWNi tím</h1>
                        <p class="text-lg text-gray-600 max-w-2xl mx-auto dark:text-txt-body">
                            GROWNi je platforma, za ktorou stojí skvelá partička. Ale aj vďaka bývalým kolegom,
                            dobrovoľníkom, expertným konzultantom a všetkým ľuďom, ktorí nám pomáhali, resp. stále
                            pomáhajú, vieme posúvať Slovensko vpred.
                            <span class="font-semibold text-[#047274] dark:text-primary-400">ĎAKUJEME.</span>
                        </p>
                    </div>

                    <!-- Photo Mosaic Grid -->
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        <!-- Large photo left -->
                        <div class="col-span-2 row-span-2">
                            <img
                                :src="teamPhoto5"
                                alt="GROWNi tím"
                                class="w-full h-full object-cover rounded-2xl cursor-pointer hover:opacity-90 transition-opacity"
                                loading="lazy"
                                @click="openLightbox(teamPhoto5)"
                            />
                        </div>
                        <!-- Top right -->
                        <div class="col-span-1">
                            <img
                                :src="teamPhoto2"
                                alt="GROWNi tím"
                                class="w-full h-48 object-cover rounded-2xl cursor-pointer hover:opacity-90 transition-opacity"
                                loading="lazy"
                                @click="openLightbox(teamPhoto2)"
                            />
                        </div>
                        <!-- Top right corner -->
                        <div class="col-span-1">
                            <img
                                :src="teamPhoto3"
                                alt="GROWNi tím"
                                class="w-full h-48 object-cover rounded-2xl cursor-pointer hover:opacity-90 transition-opacity"
                                loading="lazy"
                                @click="openLightbox(teamPhoto3)"
                            />
                        </div>
                        <!-- Bottom right -->
                        <div class="col-span-1">
                            <img
                                :src="teamPhoto1"
                                alt="GROWNi tím"
                                class="w-full h-48 object-cover rounded-2xl cursor-pointer hover:opacity-90 transition-opacity"
                                loading="lazy"
                                @click="openLightbox(teamPhoto1)"
                            />
                        </div>
                        <!-- Bottom right corner -->
                        <div class="col-span-1">
                            <img
                                :src="teamPhoto4"
                                alt="GROWNi tím"
                                class="w-full h-48 object-cover rounded-2xl cursor-pointer hover:opacity-90 transition-opacity"
                                loading="lazy"
                                @click="openLightbox(teamPhoto4)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Team Section -->
        <section class="py-16">
            <div class="container">
                <div class="max-w-5xl mx-auto">
                    <div
                        v-for="(member, index) in teamMembers"
                        :key="member.name"
                        class="py-12 border-b border-gray-100 last:border-b-0 dark:border-default"
                    >
                        <div
                            class="flex flex-col md:flex-row items-center gap-8 md:gap-12"
                            :class="{ 'md:flex-row-reverse': index % 2 === 1 }"
                        >
                            <!-- Photo -->
                            <div class="relative shrink-0">
                                <div class="relative">
                                    <!-- Decorative dots -->
                                    <div
                                        class="absolute w-24 h-24 opacity-20"
                                        :class="index % 2 === 0 ? '-top-4 -right-4' : '-top-4 -left-4'"
                                        :style="{
                                            backgroundImage: 'radial-gradient(circle, #047274 2px, transparent 2px)',
                                            backgroundSize: '12px 12px'
                                        }"
                                    ></div>
                                    <!-- Photo circle -->
                                    <div
                                        class="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden bg-gray-200 relative z-10 dark:bg-surface-subtle"
                                    >
                                        <img
                                            v-if="member.image"
                                            :src="member.image"
                                            :alt="member.name"
                                            class="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                        <div v-else class="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-surface-subtle">
                                            <svg
                                                class="w-20 h-20 text-gray-300"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Info -->
                            <div class="flex-1 text-center md:text-left">
                                <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-1 dark:text-txt-headings">
                                    {{ member.name }}
                                </h2>
                                <p class="text-sm font-semibold text-[#047274] uppercase tracking-wide mb-4 dark:text-primary-400">
                                    {{ member.role }}
                                </p>
                                <p v-if="member.description" class="text-gray-600 leading-relaxed dark:text-txt-body">
                                    {{ member.description }}
                                </p>
                                <div v-if="member.email" class="mt-4">
                                    <a
                                        :href="
                                            member.email.includes('@')
                                                ? `mailto:${member.email}`
                                                : `https://${member.email}`
                                        "
                                        class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#047274] transition-colors dark:text-txt-body dark:hover:text-primary-400"
                                        :target="member.email.includes('@') ? undefined : '_blank'"
                                        :rel="member.email.includes('@') ? undefined : 'noopener noreferrer'"
                                    >
                                        <svg
                                            v-if="member.email.includes('@')"
                                            class="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                            />
                                        </svg>
                                        <svg v-else class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path
                                                d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                                            />
                                        </svg>
                                        {{ member.email }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Ex-Team Section -->
        <section class="py-16 bg-gray-50 dark:bg-surface-subtle">
            <div class="container">
                <div class="max-w-5xl mx-auto">
                    <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center dark:text-txt-headings">Ex-kolegovia GROWNi</h2>
                    <div
                        v-for="(member, index) in exTeamMembers"
                        :key="member.name"
                        class="py-12 border-b border-gray-200 last:border-b-0 dark:border-default"
                    >
                        <div
                            class="flex flex-col md:flex-row items-center gap-8 md:gap-12"
                            :class="{ 'md:flex-row-reverse': index % 2 === 1 }"
                        >
                            <!-- Photo -->
                            <div class="relative shrink-0">
                                <div class="relative">
                                    <!-- Decorative dots -->
                                    <div
                                        class="absolute w-24 h-24 opacity-20"
                                        :class="index % 2 === 0 ? '-top-4 -right-4' : '-top-4 -left-4'"
                                        :style="{
                                            backgroundImage: 'radial-gradient(circle, #047274 2px, transparent 2px)',
                                            backgroundSize: '12px 12px'
                                        }"
                                    ></div>
                                    <!-- Photo circle -->
                                    <div
                                        class="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden bg-gray-200 relative z-10 dark:bg-surface-subtle"
                                    >
                                        <img
                                            v-if="member.image"
                                            :src="member.image"
                                            :alt="member.name"
                                            class="w-full h-full object-cover grayscale"
                                            loading="lazy"
                                        />
                                        <div v-else class="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-surface-subtle">
                                            <svg
                                                class="w-20 h-20 text-gray-300"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Info -->
                            <div class="flex-1 text-center md:text-left">
                                <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-1 dark:text-txt-headings">
                                    {{ member.name }}
                                </h2>
                                <p class="text-sm font-semibold text-[#047274] uppercase tracking-wide mb-4 dark:text-primary-400">
                                    {{ member.role }}
                                </p>
                                <p v-if="member.description" class="text-gray-600 leading-relaxed dark:text-txt-body">
                                    {{ member.description }}
                                </p>
                                <div v-if="member.email" class="mt-4">
                                    <a
                                        :href="
                                            member.email.includes('@')
                                                ? `mailto:${member.email}`
                                                : `https://${member.email}`
                                        "
                                        class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#047274] transition-colors dark:text-txt-body dark:hover:text-primary-400"
                                        :target="member.email.includes('@') ? undefined : '_blank'"
                                        :rel="member.email.includes('@') ? undefined : 'noopener noreferrer'"
                                    >
                                        <svg
                                            v-if="member.email.includes('@')"
                                            class="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                            />
                                        </svg>
                                        <svg v-else class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path
                                                d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                                            />
                                        </svg>
                                        {{ member.email }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Lightbox Modal -->
        <Teleport to="body">
            <Transition name="fade">
                <div
                    v-if="lightboxImage"
                    class="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
                    @click="closeLightbox"
                >
                    <button
                        class="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
                        @click="closeLightbox"
                    >
                        <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                    <img
                        :src="lightboxImage"
                        alt="GROWNi tím"
                        class="max-w-full max-h-[90vh] object-contain rounded-lg"
                        @click.stop
                    />
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
// Tímové fotky (hero)
import teamPhoto1 from '@/assets/images/team/tim 1.jpg'
import teamPhoto2 from '@/assets/images/team/tim 2.jpg'
import teamPhoto3 from '@/assets/images/team/tim 3.jpg'
import teamPhoto4 from '@/assets/images/team/tim 4.jpg'
import teamPhoto5 from '@/assets/images/team/tim 5.jpg'

// Aktuálny tím
import aurelImg from '@/assets/images/team/Aurel.jpg'
import barboraImg from '@/assets/images/team/Barbora Petrasova.jpg'
import lukasImg from '@/assets/images/team/Lukas Laurinec.jpg'
import petoImg from '@/assets/images/team/Peter Mucha.jpg'
import simonaImg from '@/assets/images/team/Simona.jpg'
import peterSkribaImg from '@/assets/images/team/Peter Škríba.jpg'
import peterSlovakImg from '@/assets/images/team/Peter Slovák.jpg'
import majkaImg from '@/assets/images/team/Majka kucheieva.jpg'
import svetlanaImg from '@/assets/images/team/Svetlana.jpg'
import viktoriaImg from '@/assets/images/team/Viktoria.jpg'
import lauraImg from '@/assets/images/team/laura.jpg'
import ludmilaImg from '@/assets/images/team/ludmila.jpg'
import branoImg from '@/assets/images/team/Braňo.jpg'
import michaelaImg from '@/assets/images/team/michaela ridzikova.jpg'
import karolinaImg from '@/assets/images/team/Karolina Sandalova.jpeg'
import zuzanaImg from '@/assets/images/team/Zuzana Koscikova.jpeg'
import hankaMImg from '@/assets/images/team/Hanka Mániková.png'

// Ex-kolegovia
import michalImg from '@/assets/images/team/Michal Bočkay.png'
import veronikaImg from '@/assets/images/team/Veronika Slovakova.jpg'
import roboImg from '@/assets/images/team/Robo Balušík.jpg'
import dianaImg from '@/assets/images/team/Diana Krajcirikova.jpg'
import hanaImg from '@/assets/images/team/Hana Benedikovicova.jpg'
import adamImg from '@/assets/images/team/Adam Urban.jpg'
import luciaImg from '@/assets/images/team/Lucia Rechtorovicova.jpg'
import simonaPImg from '@/assets/images/team/Simona Plevova.jpg'
import sofiaImg from '@/assets/images/team/Sofia Suranska.jpg'
import toliaImg from '@/assets/images/team/tolia tyščenko.jpg'
import peterPImg from '@/assets/images/team/Peter Popovič.jpg'
import pavlinaImg from '@/assets/images/team/Pavlína Tomašiková.jpg'
import terkaImg from '@/assets/images/team/Terka.jpg'
import viktorImg from '@/assets/images/team/Viktor Gregor.jpg'

definePageMeta({
    layout: 'landingpage'
})

useHead({
    title: 'GROWNi tím | GROWNi',
    meta: [
        {
            name: 'description',
            content: 'Spoznajte tím GROWNi - ľudí, ktorí stoja za platformou pre rast a rozvoj talentov na Slovensku.'
        }
    ]
})

// Lightbox
const lightboxImage = ref<string | null>(null)

const openLightbox = (image: string) => {
    lightboxImage.value = image
    document.body.style.overflow = 'hidden'
}

const closeLightbox = () => {
    lightboxImage.value = null
    document.body.style.overflow = ''
}

// Close on Escape key
onMounted(() => {
    const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') closeLightbox()
    }
    window.addEventListener('keydown', handleEscape)
    onUnmounted(() => window.removeEventListener('keydown', handleEscape))
})

interface TeamMember {
    name: string
    role: string
    image?: string
    description?: string
    email?: string
}

const teamMembers: TeamMember[] = [
    {
        name: 'Aurel Olle',
        role: 'Leader GROWNi',
        image: aurelImg,
        email: 'relo@growni.sk',
        description:
            'S GROWNi spolu rastieme od úplných začiatkov. Do LEAFu som pôvodne prišiel budovať online platformu s tým, že najprv si dáme pár rokov príprav na takú veľkú vec. Prišla korona a z plánovaných rokov sa stali 4 mesiace, za ktoré sme postavili GROWNi na nohy. Zodpovedal som za technickú časť, development, produkt a marketing. Dnes je to už celé GROWNi. Súčasťou mojej práce sú aj rôzne hračky: AI, GoogleCloud, Firebase, Github, Bloomreach, Ayanza, Typesense, Mailgun, LiveAgent, Figma, Hotjar, ... Vo vlastnom združení organizujeme zážitkové aktivity pre deti (v zime ako lyžiarsky inštruktor, v lete ako vodácky sprievodca). Milujem radosť, agilnosť, open source systémy, Google a po novom už aj Apple produkty a parádnych kolegov @LEAF a @GROWNi.'
    },
    {
        name: 'Barbora Petrášová',
        role: 'Volunteering & Data & Scrum Masterka',
        image: barboraImg,
        email: 'barbora.petrasova@leaf.sk',
        description:
            'Vyštudovala som sociológiu na Univerzite Komenského v Bratislave a časť štúdia som strávila vo Švédsku, na skok od polárneho kruhu. Potom som sa išla rozmraziť do Austrálie, kde som žila pár rokov, cestovala a manažovala kaviareň s 50-timi druhmi zmrzliny. Vrátila som sa domov a našla LEAF. Najskôr som pracovala v operations a potom v Programe expertného dobrovoľníctva. Ten je už dnes v novej podobe súčasťou GROWNi. Na GROWNi sa okrem rozvoja dobrovoľníckej sekcie, venujem aj dátovej analýze. Voľný čas venujem spánku. Keď nespím, cvičím jógu, vyrábam mydlá, rozmaznávam svojho psa Šaňa, cestujem a spoznávam iné svety ľudí. Momentálne sa venujem stavbe slameného domu vo svojom rodisku.'
    },
    {
        name: 'Lukáš Laurinec',
        role: 'Marketing',
        image: lukasImg,
        email: 'lukas.laurinec@leaf.sk',
        description:
            'Takže ja som ten typ, čo stále vidí pohár poloplný. Vyštudoval som manažment a marketing a vždy som chcel v tejto oblasti pracovať, čo sa mi po škole nie vždy darilo. Idem, riešim a v GROWNi sa starám o komunikáciu. Mojim koníčkom je študovanie nových vecí, a ak ma niečo zaujme, tak chcem o tom vedieť čo najviac – idem do hĺbky, rozoberám na drobné, skúmam, analyzujem. Zaujímajú ma technológie, veda, vesmír, marketing, manažment, environmentálne témy, zdravie. Milujem hudbu, hory, ľudí a spánok. A aby som nezabudol, som environmentalista, záleží mi na prostredí, do ktorého vychovávam svoje potomstvo. Ale počkaj, to nie je všetko – milujem scifi, občas si pripadám, že sám som z iného sveta. Stopárov sprievodca galaxiou je musthave, preto nezabudnite cestovať vždy s uterákom, a že 42 je odpoveďou na najdôležitejšiu otázku života, vesmíru a toho všetkého. A asi to nedáva zmysel, či? :wink: Rád sa zamýšľam nad vecami, možno som skrytý filozof (smiech v pozadí). Celkovo som taký nadšenec s nekonečným optimizmom, ktorý chce doniesť trošku pozitívnej zmeny a urobiť niečo pre našu malebnú krajinku pod drobnými veľhorami. A aj to je hlavným dôvodom prečo som sa ocitol na GROWNi – poďme zmeniť svet okolo seba, nikto iný to za nás neurobí.'
    },
    {
        name: 'Simona Škvareninová',
        role: 'Sociálne siete',
        image: simonaImg,
        email: 'simona.skvareninova@leaf.sk',
        description:
            'V GROWNi mám pod palcom sociálne siete – od TikToku cez Instagram a Facebook až po LinkedIn. Moja práca nie je len o tvorbe videí, ale o kompletných kampaniach a nápadoch.\nOkrem digitálneho sveta ma už tri roky môžete počuť aj v éteri rádia Beta v Bojniciach. Moderovanie je moja srdcovka a skvelá škola pohotovosti, ktorá ma drží v strehu. Fascinácia médiami ma sprevádza už od výšky FMK v Trnave a som rada, že som sa v tomto kreatívnom kolotoči našla a môžem sa v ňom naplno realizovať.\nKeď práve neriešim posty alebo nie som za mikrofónom, som typický „požierač" príbehov. Buď ma nájdete s dobrou knihou v ruke, alebo scrollujem streamovacie platformy a hľadám nové filmové či seriálové klenoty.'
    },
    {
        name: 'Svetlana Tarageľová',
        role: 'Leader GROWNi (na materskej)',
        image: svetlanaImg,
        description:
            'Pracovala som v startupoch a scaleupoch vďaka čomu som si vyskúšala takmer všetky pozície na šírku aj na hĺbku. Koordináciu teamov – oddelení a nakoniec aj celej firmy s obratom 1,5 mil €. Nasledoval skvelý "bootcamp" v Exponea (aktuálne Bloomreach), kde som bola obklopená špičkovými špecialistami a pomáhala s expanziou slovenskej firmy po celom svete. Potom som náhodou a na chvíľu (ako som si myslela) prišla do neziskovky LEAF, práve v čase začiatku korony, kedy prišlo rozhodnutie hneď zmeniť kormidlo a presunúť sa online – trebalo vybudovať od piky nový produkt ako digitálnu platformu. So zachovaním si startup culture aj v rámci NGO prostredia ho budujeme nakoniec už roky aj cez plnú radu výziev, ktoré paradoxne ďaleko presahujú výzvy bežných e-shopov alebo iných digitálnych produktov.'
    },
    {
        name: 'Ľudmila Kvašňovská',
        role: 'Pre študentov',
        image: ludmilaImg,
        email: 'ludmila.kvasnovska@leaf.sk',
        description:
            'Študujem medicínu a už od strednej školy sa venujem vede a výskumu. Vždy ma však zaujímali aj spoločenské témy a podpora mladých v ich rozvoji. Na gymnáziu som sa venovala zdieľaniu príležitostí pre stredoškolákov a pomáhala spolužiakom získavať stáže a zahraničné pobyty. Dnes na tejto téme pracujem v GROWNi v rámci sekcie Pre stredoškolákov, kde študenti nájdu príležitosti pre svoj osobnostný a akademický rozvoj. Verím, že kvalitné a rozmanité vzdelávanie je základom pokroku spoločnosti.'
    },
    {
        name: 'Braňo Kleskeň',
        role: 'Leader LEAF',
        image: branoImg,
        description:
            'Ako bývalý partner globálnej poradenskej spoločnosti McKinsey & Company mám rozsiahle skúsenosti z niekoľkých kontinentov so zlepšovaním výkonnosti korporácií, rozvojom nových firiem a skvalitňovaním vzdelávania. Svoju energiu naberám z užívania si prítomnosti: mám ženu a tri deti, som bývalý a občasný hokejista a amatérsky hudobník, a zo snímania o budúcnosti: učenie sa, budovanie nových iniciatív a využívanie ľudského potenciálu.'
    },
    {
        name: 'Michaela Ridziková',
        role: 'Partnership',
        image: michaelaImg,
        email: 'michaela.ridzikova@leaf.sk',
        description:
            'Pracovné (i životné) skúsenosti som mala možnosť nabrať v nezisku aj korporátoch. Verím, že Slovensko má potenciál posunúť sa vpred, ak príde k zmysluplnému prepájaniu a spájaniu ľudí z rôznych sektorov. Aj preto som mala v GROWNi na starosti budovanie a zveľaďovanie partnerstiev s firmami, verejným sektorom a neziskovkami. Vyštudovala som Blízkovýchodné štúdiá, vďaka ktorým som mala možnosť spoznať kúsok "iného" sveta, jazyka, kultúry, a tak sa naučiť premýšľať o veciach kriticky, s nadhľadom a možno väčšou pokorou. Po návrate na Slovensko som pracovala v mládežníckom sektore a veľkej technologickej firme, kde som mala na starosti manažovanie zaujímavých projektov s dopadom na zdravie a wellbeing ľudí.'
    },
    {
        name: 'Karolína Šandalová',
        role: 'Partnership',
        image: karolinaImg,
        email: 'karolina@growni.sk',
        description:
            'Baví ma prepájať firmy s ľuďmi a príležitosťami, ktoré dávajú zmysel. V GROWNi pôsobím ako Partnership Builder a okrem rozvoja partnerstiev mám na starosti aj pracovné ponuky a stáže, ktoré pomáhajú mladým ľuďom urobiť prvý (alebo ďalší) krok v kariére. V neziskovom sektore sa pohybujem od roku 2020, keď som začala pracovať pre zahraničnú neziskovú organizáciu. Práve tam som si uvedomila, že ma najviac baví prepájať ľudí a vytvárať spolupráce, ktoré dávajú zmysel - čo dnes robím aj v GROWNi. Vyštudovala som psychológiu a pracujem aj ako koučka, čo sa prirodzene odráža v tom, ako pristupujem k ľuďom a spoluprácam. Na GROWNi ma najviac baví, že rast tu nie je len o kariére, ale aj o ľuďoch, vzťahoch, hodnotách a o tom, akým človekom sa popri tom stávaš. Ak práve nepracujem, možno krmím kapybary, lyžujem alebo skúšam novú zmrzlinu v meste.'
    },
    {
        name: 'Zuzi Koščíková',
        role: 'Dobrovoľníctvo & Mentoring',
        image: zuzanaImg,
        email: 'zuzi.koscikova@leaf.sk',
        description:
            'Aj keď je môj background v ekológii, environmentalistike a udržateľnom plánovaní miest, GROWNi si ma aj tak nejak našlo. Niekoľko krát sa ma pýtali, či som si istá, že idem do toho, že to nie je enviro sektor. No ja vravím, že udržateľnosť nie je len o prírode, ale aj o ľuďoch. Cez zmysluplné prepojenia tých správnych ľudí a organizácií môže stále vzniknúť projekt, ktorý odráža sociálnu, environmentálnu a aj finančnú udržateľnosť. Baví ma príliš veľa rôznych vecí na to, aby som bola špecialista. Popri mojich hlavných smeroch som študovala aj podnikanie, umenie alebo cirkulárnu ekonomiku. Väčšinu času som niekde na cestách a fotím všetko naokolo. Keď som sa po strednej rozhodla, že idem študovať do zahraničia, vravela som, ako sa vrátim a prinesiem všetky tie nové cool veci na Slovensko. Well, seems like GROWNi made it happen?'
    },
    {
        name: 'Hana Benedikovičová',
        role: 'Intern',
        image: hanaImg,
        description:
            'Láska ku vzdelávaniu a sebarozvoju ju dostala do neziskového sektora. Ako stredoškoláčka absolvovala program DASATO Academy. Našla radosť v prepájaní ľudí a ich posúvaní vpred. V GROWNi pracovala na formáte pracovných skupín GROWNi GROWps. Vo voľnom čase rada číta, navštevuje galérie, hudbu a festivaly.'
    },
    {
        name: 'Hanka Mániková',
        role: 'Intern',
        image: hankaMImg
    },
    {
        name: 'Simona Adamusová',
        role: 'Intern'
    }
]

const exTeamMembers: TeamMember[] = [
    {
        name: 'Terka Mihalovič Lukáčová',
        role: 'Co-Founder',
        image: terkaImg,
        description:
            'Jej snom je dlhodobé zlepšovanie vzdelávacieho systému na Slovensku. Na GROWNi sa venovala základným rámcom platformy, vzdelávaciemu streamu, mentoringmu, partnerstvám s organizáciami a GROWps formátu. Je to veľká platforma s malým a agilným tímom. Na 2-ročnom štúdiu v Talline skúmala ako zlepšiť slovenské školstvo podľa estónskeho modelu. Zbožňuje hory, prírodu, umenie, hudbu, divadlo, lyže, lezenie a ľudí.'
    },
    {
        name: 'Peter Škríba',
        role: 'ex Developer',
        image: peterSkribaImg
    },
    {
        name: 'Peťo Mucha',
        role: 'ex Developer',
        image: petoImg,
        email: 'linkedin.com/in/peter-mucha/',
        description:
            'Aj keď som neštudoval informatiku, programovanie a tvorba webových stránok ma popri škole tak začalo baviť, že ma vtiahlo ku sebe úplne. Preskákal som si aj "čisté" PHPéčko, aj responzívny dizajn v HTML a CSS, až som nakoniec zakotvil v prístave Front-endu. V GROWNi ma každé ráno čaká na stole plný tanier nových vylepšení platformy a aj poloprázdny pohár bugov. Každý deň sa v našom development tíme snažíme posúvať kvalitu GROWNi vyššie a vyššie, až do nebies 🙂 Vo voľnom čase sa starám aj o produkčnú časť vinárskych podujatí u nás v Pezinku a okolí. Pomáham ku lepšiemu vinohradnícko-gastronomickému zážitku návštevníkov podujatí ako sú Malé vínne uličky, Víno pod hviezdami a iné. Ak mi popri všetkých aktivitách ostane nejaký ten čas, zacvičím si jógu, zahrám na bassgitare alebo vybehnem na bicykel.'
    },
    {
        name: 'Majka Kucheieva',
        role: 'ex Developer',
        image: majkaImg,
        description: 'At GROWNi, I center divs and move buttons. In my free time, I do the same — just for fun.'
    },
    {
        name: 'Peter Slovák',
        role: 'ex Partnership',
        image: peterSlovakImg,
        email: 'peter.slovak@leaf.sk'
    },
    {
        name: 'Viktória Malinčáková',
        role: 'ex Dobrovoľníctvo & Mentoring',
        image: viktoriaImg,
        description:
            'Neziskový sektor som objavila počas stredoškolského štúdia cez rôzne vzdelávacie programy ako je napr. DofE. Po nástupe na univerzitu, kde som študovala Európske štúdia som sa neustále venovala sebarozvoju. Absolvovala som Nexteria Leadership Academy a pracovala na rôznych projektoch vo viacerých neziskových organizáciách. Počas mojej cesty neziskovkami mi pomohli viacerí mentori a mentorky, ktorí mi pomohli sa posunúť vpred. Oblasť vzdelávania, sebarozvoja a kreativity je to, čo má napĺňa. V GROWNi sa venujem sekcii mentoringu a dobrovoľníctva.'
    },
    {
        name: 'Laura Laurinčíková',
        role: 'ex Pracovné skupiny GROWps',
        image: lauraImg,
        description:
            'Počas mojho doterajšieho života som mala možnosť vyskúšať viaceré rozvojové oblasti – od nášho sloveského folklóru, cez rozličné umelecké a športové smery, až po svet golfu. Na strednej škole som sa po prvýkrát začala ponárať do vôd neziskového sektora. Vďaka ročnému vzdelávaciemu programu DASATO, kde som neskôr pôsobila aj ako členka tímu, som za veľmi krátku dobu pochopila krásu a zmysel tejto formy aktivity. Aktuálne študujem ekonómiu v Dánsku a v GROWNi mám na starosti koordináciu pracovných skupín. Mojou úlohou je prepájať organizácie a šikovných profesionálov, ktorí sa chcú podeliť so svojimi skúsenosťami s ľuďmi so záujmom napredovať. Táto práca ma neustále obohacuje o nové poznatky a stretnutia, ale najmä mi dáva pocit, že byť súčasťou vzdelávania Slovenska má zmysel. :))'
    },
    {
        name: 'Michal Bočkay',
        role: 'ex Leader GROWNi',
        image: michalImg,
        description:
            'Ak by som mal vybrať jednu vec, ktorá ma naozaj baví, tak je to práca s dátami. V kombinácii so skúsenosťami z business developmentu, financií a vývoja digitálnych produktov a záujmom o kreatívne a komunikačné zadania tak som pomáhal GROWNi na ceste k finančnej udržateľnosti. Je to výzva, no vždy si rád nájdem čas na šport, cestovanie a chvíle s rodinou.'
    },
    {
        name: 'Veronika Slováková',
        role: 'ex Jobs & Partnership',
        image: veronikaImg,
        email: 'veronika.slovakova@leaf.sk',
        description:
            'V tíme GROWNi som pomerne krátko, ale mám pocit, že som našla presne to, čo som hľadala. Podarilo sa mi na mojej pozícii produkťáčky skĺbiť moje pracovné skúsenosti z verejného a súkromného sektora z oblastí HR a IT. Zároveň som našla skvelý tím ľudí, ktorých práca rovnako baví a veria tomu, že svojim malým kúskom prispejú k lepším zajtrajškom. Vyštudovala som Európske štúdiá na Slovensku a istý čas štúdia som pobudla aj vo Francúzsku a v Belgicku. Po niekoľkých rokov v zahraničí som sa vrátila domov.'
    },
    {
        name: 'Robo Balušík',
        role: 'ex Slováci v zahraničí',
        image: roboImg,
        email: 'linkedin.com/in/robert-balusik',
        description:
            'Prvá časť života v umeleckom duchu (umelecký prednes, dabing, herectvo), neskôr ho učarovala technológia. Na strednej spolozaložil IT firmu, študoval Aplikovanú informatiku v Olomouci. Pracoval pre 140+ klientov od startupov po firmy s desiatkami miliónov obratu. Pomáha prepájať Slovákov s globálnymi mentormi a pozdvihuje startup ekosystém. Rád strieda adrenalín s relaxom v prírode, miluje hudbu a osobnostný rozvoj.'
    },
    {
        name: 'Diana Krajčíriková',
        role: 'ex Product Manager GROWps',
        image: dianaImg,
        description:
            'Vyštudovala právo ale chytilo ju IT srdce. Venuje sa vývoju digitálneho produktu – GROWNi aplikácie. Pracovala ako Scrum Master s presahom na PM a Product Ownership. Rada behá do hôr, navštevuje kaviarne s filtrovanou kávou. Miluje čas s blízkymi, smiech a indickú kuchyňu.'
    },
    {
        name: 'Adam Urban',
        role: 'ex Slovak students abroad',
        image: adamImg,
        description:
            'Veľmi random človek, ktorý vždy povie „jasné, kedy?" na akýkoľvek nápad. Vždy sa rád vracia domov na Slovensko napriek tomu, že cestuje. V GROWNi ho bavilo, že tu pracujú zaujímaví a kompetentní ľudia a ich práca má zmysel a reálny dopad. Veria na lepšie Slovensko a čím lepšie sa budeme mať, tým šťastnejší bude.'
    },
    {
        name: 'Lucia Rechtorovičová',
        role: 'ex GROWNi/LEAF',
        image: luciaImg,
        description:
            'Už počas školy sa ťahala k neziskovému sektoru a komunitným projektom. Po roku dobrovoľníctva na Kanárskych pracovala ako PM v oblasti vzdelávania a štipendií. V LEAFe začínala v Programe expertného dobrovoľníctva, na GROWNi sa venovala mentoringu - hľadala spôsoby ako ho produktovo zlepšovať. Má rada dobrodružstvá s rodinou, hory, splavy, knihy a podcasty.'
    },
    {
        name: 'Simona Plevová',
        role: 'ex Marketing (na materskej)',
        image: simonaPImg,
        description:
            'Po vysokej škole, kde bola aktívna v AIESEC, pracovala ako social media specialist v agentúre Zaraguza dva roky. Neziskový svet si ju vrátil do LEAF a GROWNi kde sa starala o marketing a komunikáciu. Teraz pauzuje. Cestuje, športuje, číta, miluje hory a turistiku, dokazuje že tancovať sa dá naučiť sa v akomkoľvek veku.'
    },
    {
        name: 'Sofia Šuranská',
        role: 'ex Marketing',
        image: sofiaImg,
        description:
            'Cez svoju proaktivitu počas strednej školy sa ocitla v neziskových organizáciách. Počas vedení študentskej firmy absolvovala program DASATO. Vystriedala si pozície a stážovala v Slide. V GROWNi bola na sociálnych sieťach a koordinovala aj DASATO socials. Venuje sa posúvaniu mladých ľudí vpred. Behá horami s foťákom, je dobrovoľníčka a organizuje podujatia.'
    },
    {
        name: 'Tolia Tyščenko',
        role: 'ex Developer',
        image: toliaImg
    },
    {
        name: 'Peter Popovič',
        role: 'ex Partnership builder',
        image: peterPImg,
        description:
            'Fašinuje ho vidieť perspektívne projekty rásť. Študoval medzinárodný obchod a marketing v Kodani kde videl malé projekty rásť na firmy. Pracoval v hodnotových startupoch a scale-upoch. Vybudoval svoj vlastný projekt nu3o.com. Rád sa venuje kvalitnému odpočinku, spoločenským aktivitám, športom, turistike a dobrým ľuďom.'
    },
    {
        name: 'Pavlína Tomášiková',
        role: 'ex Marketing, sociálne siete',
        image: pavlinaImg,
        description:
            'V GROWNi sa starala o copywriting, social media a marketing. Už ako malá sa zamilovala do literatúry a jej láska k čítaniu prerástla do vášne písania. Vedela, že sa chce venovať písaniu. Práca v GROWNi ju bavila pretože spájala jej vášeň s pomocou iným a Slovensku. Kreatívna duša, ukecaná extrovertka, ktorá dlho súťažne plávala, teraz ju chytilo surfovanie a cestovanie. Sníva o presťahovaní sa k moru.'
    },
    {
        name: 'Viktor Gregor',
        role: 'Expertný dobrovoľník – Data',
        image: viktorImg,
        description:
            'Študoval aplikovanú matematiku v Bratislave a Amsterdame. V práci sa venuje dátovej analýze mobilných hier. Pre GROWNi pomáha ako dobrovoľník s dátami pre interný reporting. Pracuje s SQL, BigQuery a reporting nástrojmi.'
    }
]
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
